import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw, Plus, AlertTriangle, X, Loader2, Users,
  Calendar, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const COHORT_STATUS = {
  planning:  { label: 'In Planung',    cls: 'bg-blue-100 text-blue-700' },
  active:    { label: 'Aktiv',         cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Abgeschlossen', cls: 'bg-emerald-100 text-emerald-700' },
  archived:  { label: 'Archiviert',    cls: 'bg-gray-100 text-gray-500' },
};

const COHORT_TYPES = {
  modul_1:        { label: 'Modul 1 — KI Ready Check' },
  modul_2:        { label: 'Modul 2 — KI Strategie Workshop' },
  modul_1_und_2:  { label: 'Modul 1 + 2 (Kombi)' },
};

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const DOMAIN_LABELS = {
  prozess: 'Prozesse',
  controlling: 'Controlling',
  operations: 'Operations',
  marketing: 'Marketing',
  strategie: 'Strategie',
  hr: 'HR',
};

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
  catch { return '—'; }
}

// Re-exports für KundenModuleBoard
export { NewCohortModal, CohortDetailModal, COHORT_TYPES, COHORT_STATUS, WEEKDAYS, DOMAIN_LABELS, formatDate };

export default function CohortsAdmin() {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [activeCohortId, setActiveCohortId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/cohorts`, { headers: authHeaders() });
      setCohorts(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Modul-1-Kohorten
          </h1>
          <p className="text-sm text-gray-500">Gruppen von 8-15 Kunden, die gemeinsam Modul 1 durchlaufen. Termine gelten für alle Mitglieder gleichzeitig.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-gray-50">
            <RefreshCw size={14} /> Neu laden
          </button>
          <button onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a]"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={14} /> Neue Kohorte
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm"><AlertTriangle size={14} className="inline mr-2" /> {error}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400">Lade…</div>
      ) : cohorts.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400">
          Noch keine Kohorten. Klick auf "Neue Kohorte" zum Anlegen.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cohorts.map((c) => {
            const meta = COHORT_STATUS[c.status] || COHORT_STATUS.planning;
            return (
              <div key={c.id} onClick={() => setActiveCohortId(c.id)}
                className="border border-gray-200 bg-white p-4 cursor-pointer hover:border-[#658a07]">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{c.name}</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-[#658a07] mb-1 font-bold">{(COHORT_TYPES[c.cohort_type] || COHORT_TYPES.modul_1).label}</p>
                <p className="text-xs text-gray-500 mb-2 line-clamp-2">{c.description || '—'}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Users size={11} /> {c.member_count} Kunden</span>
                  {c.recurring_weekday !== null && c.recurring_weekday !== undefined && c.recurring_time && (
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {WEEKDAYS[c.recurring_weekday]} {c.recurring_time}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NewCohortModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); load(); setActiveCohortId(c.id); }} />}
      {activeCohortId && <CohortDetailModal cohortId={activeCohortId} onClose={() => { setActiveCohortId(null); load(); }} />}
    </div>
  );
}

function NewCohortModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [cohortType, setCohortType] = useState('modul_1');
  const [description, setDescription] = useState('');
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringWeekday, setRecurringWeekday] = useState('1');   // Dienstag default
  const [recurringTime, setRecurringTime] = useState('10:00');
  const [durationMinutes, setDurationMinutes] = useState(45);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/cohorts`, {
        name: name.trim(),
        cohort_type: cohortType,
        description: description.trim(),
        recurring_start_date: recurringStartDate || undefined,
        recurring_weekday: recurringStartDate ? parseInt(recurringWeekday, 10) : undefined,
        recurring_time: recurringStartDate ? recurringTime : undefined,
        duration_minutes: parseInt(durationMinutes, 10),
      }, { headers: authHeaders() });
      onCreated(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[560px] max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex justify-between items-start sticky top-0">
          <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>Neue Kohorte / Modul</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm">
            <AlertTriangle size={14} className="inline mr-2" /> {error}
          </div>
        )}

        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-xs text-gray-600">
            4 Gruppen-Termine werden automatisch angelegt. Wenn du Serien-Datum + Wochentag + Uhrzeit angibst,
            generiert das System auch Google-Calendar-Events mit Meet-Link + 24h-Erinnerung — Kunden bekommen
            beim Hinzufügen automatisch Calendar-Einladungen.
          </p>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Modul-Typ *</label>
            <select value={cohortType} onChange={(e) => setCohortType(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              {Object.entries(COHORT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200}
              placeholder="z.B. Modul 1 — Juni 2026 Kohorte"
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Beschreibung</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none" />
          </div>

          <div className="border border-gray-200 p-3 space-y-3 bg-gray-50/30">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Serientermin (Wochentag + Uhrzeit)
            </p>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Ab Datum</label>
              <input type="date" value={recurringStartDate} onChange={(e) => setRecurringStartDate(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              <p className="text-[10px] text-gray-400 mt-1">Erster Gruppen-Termin am nächsten gewählten Wochentag ab diesem Datum. 4 Wiederholungen wöchentlich.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Wochentag</label>
                <select value={recurringWeekday} onChange={(e) => setRecurringWeekday(e.target.value)}
                  disabled={!recurringStartDate}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50">
                  {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Uhrzeit</label>
                <input type="time" value={recurringTime} onChange={(e) => setRecurringTime(e.target.value)}
                  disabled={!recurringStartDate}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a] disabled:opacity-50" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Dauer (min)</label>
                <input type="number" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)}
                  min={15} max={240}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f]"
              style={{ fontFamily: "'Oswald', sans-serif" }}>Abbrechen</button>
            <button type="submit" disabled={busy || !name.trim()}
              className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {busy ? <><Loader2 size={12} className="animate-spin" /> Lege an…</> : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CohortDetailModal({ cohortId, onClose, inline = false, onMemberClick = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingSerie, setEditingSerie] = useState(false);
  const [serieStartDate, setSerieStartDate] = useState('');
  const [serieWeekday, setSerieWeekday] = useState('1');
  const [serieTime, setSerieTime] = useState('10:00');
  const [serieDuration, setSerieDuration] = useState(45);

  // Lade Mitarbeiter (echte CRM-User, KEINE AI-Agenten)
  useEffect(() => {
    axios.get(`${API}/auth/users`, { headers: authHeaders() })
      .then(({ data }) => setUsers((data || []).filter((u) => ['admin', 'sales', 'customer_manager'].includes(u.role))))
      .catch(() => setUsers([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cohorts/${cohortId}`, { headers: authHeaders() });
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, [cohortId]);

  useEffect(() => { load(); }, [load]);

  const toggleMilestone = async (m) => {
    setBusyId(m.id);
    try {
      await axios.patch(`${API}/admin/cohort-milestones/${m.id}`, { done: !m.done_at }, { headers: authHeaders() });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
    setBusyId(null);
  };

  const removeMember = async (rcId) => {
    if (!window.confirm('Kunde aus dieser Kohorte entfernen? Ready-Check + Sessions bleiben erhalten.')) return;
    try {
      await axios.post(`${API}/admin/cohorts/${cohortId}/remove-member?ready_check_id=${rcId}`, {}, { headers: authHeaders() });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
  };

  const startPipeline = async (rcId, companyName) => {
    if (!window.confirm(`Multi-Agent-Pipeline für "${companyName}" starten? Läuft 2-5 Minuten im Hintergrund.`)) return;
    setBusyId(rcId);
    try {
      await axios.post(`${API}/admin/cohorts/${cohortId}/start-pipeline?ready_check_id=${rcId}`, {}, { headers: authHeaders() });
      await load();
      alert('Pipeline gestartet. Status pollt sich selbst — schau in 2-5 Min wieder rein.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Pipeline-Start fehlgeschlagen.');
    }
    setBusyId(null);
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/admin/cohorts/${cohortId}`, { status: newStatus }, { headers: authHeaders() });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
  };

  const updateOwner = async (newOwnerId) => {
    try {
      await axios.patch(`${API}/admin/cohorts/${cohortId}`, { owner_user_id: newOwnerId }, { headers: authHeaders() });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
  };

  const saveSeries = async () => {
    if (!serieStartDate) {
      alert('Startdatum bitte angeben');
      return;
    }
    try {
      await axios.patch(`${API}/admin/cohorts/${cohortId}`, {
        recurring_start_date: serieStartDate,
        recurring_weekday: parseInt(serieWeekday, 10),
        recurring_time: serieTime,
        duration_minutes: parseInt(serieDuration, 10),
      }, { headers: authHeaders() });
      setEditingSerie(false);
      await load();
      alert('Serientermin gespeichert. Die scheduled_at-Daten werden bei nächstem Mitglied-Beitritt zu Google-Calendar-Events.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
  };

  // Wenn Cohort geladen: Initial-Werte für Serien-Editor füllen
  useEffect(() => {
    if (data?.cohort) {
      setSerieStartDate(data.cohort.recurring_start_date || '');
      setSerieWeekday(data.cohort.recurring_weekday !== null && data.cohort.recurring_weekday !== undefined
        ? String(data.cohort.recurring_weekday) : '1');
      setSerieTime(data.cohort.recurring_time || '10:00');
      setSerieDuration(data.cohort.duration_minutes || 45);
    }
  }, [data]);

  const deleteCohort = async () => {
    if (!window.confirm('Kohorte wirklich löschen? Nur möglich wenn keine Kunden mehr drin sind.')) return;
    try {
      await axios.delete(`${API}/admin/cohorts/${cohortId}`, { headers: authHeaders() });
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler');
    }
  };

  // Inline-Wrapper (Side-by-Side innerhalb KundenModuleBoard) vs. Overlay (Standalone-Modal).
  const wrapInline = (content) => (
    <div className="bg-white h-full flex flex-col border-r border-gray-200 overflow-y-auto">{content}</div>
  );
  const wrapOverlay = (content) => (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[920px] max-h-[calc(100vh-1rem)] overflow-y-auto">{content}</div>
    </div>
  );
  const wrap = inline ? wrapInline : wrapOverlay;

  if (loading) {
    return wrap(<div className="p-12 text-center"><Loader2 size={28} className="animate-spin mx-auto text-gray-400" /></div>);
  }

  if (!data) {
    return wrap(
      <div className="p-6">
        <p className="text-red-700">{error || 'Nicht gefunden'}</p>
        <button onClick={onClose} className="mt-3 px-3 py-2 text-xs uppercase tracking-wider border border-gray-300">Schließen</button>
      </div>
    );
  }

  const cohort = data.cohort;
  const meta = COHORT_STATUS[cohort.status] || COHORT_STATUS.planning;

  const body = (<>
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex justify-between items-start sticky top-0 z-10">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Kohorte · {data.members.length} Mitglieder
            </p>
            <h3 className="text-lg font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>{cohort.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
            <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Verantwortlicher */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Verantwortlicher (Mitarbeiter)
            </h4>
            <select value={cohort.owner_user_id || ''} onChange={(e) => updateOwner(e.target.value || null)}
              className="w-full md:w-auto border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
              <option value="">— Niemand zugewiesen —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} ({u.role})
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">Nur echte Mitarbeiter (Admin, Sales, Customer-Manager) — keine AI-Agenten.</p>
          </section>

          {/* Serientermin-Editor */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Serientermin · 4 wöchentliche Gruppen-Termine
              </h4>
              {!editingSerie && (
                <button onClick={() => setEditingSerie(true)}
                  className="text-[10px] uppercase tracking-wider border border-gray-300 px-3 py-1.5 hover:bg-gray-50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {cohort.recurring_start_date ? 'Bearbeiten' : 'Festlegen'}
                </button>
              )}
            </div>
            {!editingSerie ? (
              cohort.recurring_start_date ? (
                <p className="text-sm text-gray-700">
                  Ab <strong>{formatDate(cohort.recurring_start_date)}</strong>,
                  jeden <strong>{WEEKDAYS[cohort.recurring_weekday]}</strong> um
                  <strong> {cohort.recurring_time} Uhr</strong>
                  ({cohort.duration_minutes || 45} Min). Google-Meet + 24h-Reminder automatisch.
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">Noch nicht festgelegt. Klick "Festlegen".</p>
              )
            ) : (
              <div className="bg-gray-50 border border-gray-200 p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Ab Datum</label>
                    <input type="date" value={serieStartDate} onChange={(e) => setSerieStartDate(e.target.value)}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Wochentag</label>
                    <select value={serieWeekday} onChange={(e) => setSerieWeekday(e.target.value)}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]">
                      {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Uhrzeit</label>
                    <input type="time" value={serieTime} onChange={(e) => setSerieTime(e.target.value)}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Dauer (min)</label>
                    <input type="number" value={serieDuration} onChange={(e) => setSerieDuration(e.target.value)} min={15} max={240}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button onClick={() => setEditingSerie(false)}
                    className="text-[10px] uppercase tracking-wider px-3 py-1.5 text-gray-500 hover:text-[#1f1f1f]"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>Abbrechen</button>
                  <button onClick={saveSeries}
                    className="bg-[#0a0a0a] text-[#befa0a] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a]"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>Speichern</button>
                </div>
              </div>
            )}
          </section>

          {/* Status-Steuerung */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-2">
              {Object.entries(COHORT_STATUS).map(([k, v]) => (
                <button key={k} onClick={() => updateStatus(k)} disabled={cohort.status === k}
                  className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                    cohort.status === k ? 'bg-[#0a0a0a] text-[#befa0a] border-[#0a0a0a]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  } disabled:cursor-default`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {v.label}
                </button>
              ))}
            </div>
            {data.members.length === 0 && (
              <button onClick={deleteCohort}
                className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                <Trash2 size={11} /> Löschen
              </button>
            )}
          </div>

          {/* Termine */}
          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Gruppen-Termine (gelten für alle Mitglieder)
            </h4>
            <ol className="space-y-2">
              {data.milestones.map((m) => {
                const done = !!m.done_at;
                const unlocks = (m.unlocks_domains || []).map((d) => DOMAIN_LABELS[d] || d).join(', ');
                return (
                  <li key={m.id} className={`flex items-start gap-2 p-3 rounded ${done ? 'bg-emerald-50/40 border border-emerald-200' : 'bg-gray-50/40 border border-gray-200'}`}>
                    <button onClick={() => toggleMilestone(m)} disabled={busyId === m.id}
                      className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center text-[10px] font-bold ${
                        done ? 'bg-[#658a07] border-[#658a07] text-white' : 'bg-white border-gray-300 hover:border-[#658a07]'
                      } disabled:opacity-50`}>
                      {done ? '✓' : ''}
                    </button>
                    <div className="flex-1">
                      <p className={`text-sm font-bold ${done ? 'text-emerald-800' : 'text-[#1f1f1f]'}`}>{m.step_order}. {m.title}</p>
                      {m.description && <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>}
                      {m.scheduled_at && (
                        <p className="text-[11px] text-gray-600 mt-1">
                          <Calendar size={10} className="inline mr-1" />
                          {new Date(m.scheduled_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr
                          {m.duration_minutes ? ` (${m.duration_minutes} min)` : ''}
                        </p>
                      )}
                      {m.meeting_url && (
                        <p className="text-[11px] mt-0.5">
                          <a href={m.meeting_url} target="_blank" rel="noreferrer" className="text-[#658a07] hover:underline">
                            Google Meet öffnen
                          </a>
                        </p>
                      )}
                      {unlocks && (
                        <p className="text-[11px] text-[#658a07] mt-1">Schaltet frei: <strong>{unlocks}</strong></p>
                      )}
                      {done && (
                        <p className="text-[11px] text-emerald-600 mt-0.5">
                          Erledigt: {new Date(m.done_at).toLocaleDateString('de-DE')}{m.done_by ? ` · ${m.done_by}` : ''}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          {/* Mitglieder */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Mitglieder ({data.members.length})
              </h4>
              <button onClick={() => setShowAddMember(true)}
                className="text-[10px] uppercase tracking-wider bg-[#0a0a0a] text-[#befa0a] px-3 py-1.5 hover:bg-[#befa0a] hover:text-[#0a0a0a] inline-flex items-center gap-1"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                <Plus size={11} /> Kunden hinzufügen
              </button>
            </div>
            {data.members.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Noch keine Kunden. Klick auf "Kunden hinzufügen" oben rechts.</p>
            ) : (
              <ul className="space-y-1">
                {data.members.map((m) => {
                  const total = m.sessions_total || 0;
                  const approved = m.sessions_approved || 0;
                  const awaiting = m.sessions_awaiting_approval || 0;
                  const ready = !!m.pipeline_ready;
                  const isRunning = m.status === 'pipeline_running';
                  const isDone = ['draft_ready', 'reviewed', 'delivered'].includes(m.status);
                  return (
                    <li key={m.id}
                      onClick={onMemberClick ? () => onMemberClick(m) : undefined}
                      className={`flex items-center justify-between p-2 border border-gray-200 ${onMemberClick ? 'cursor-pointer hover:border-[#658a07] hover:bg-[#658a07]/5' : ''}`}
                      title={onMemberClick ? 'Klick: Kunde öffnen (Details + 1:1-Termin)' : ''}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-[#1f1f1f]">{m.company_name || '—'}</p>
                        <p className="text-[11px] text-gray-500 font-mono truncate">{m.run_id}</p>
                        {total > 0 && (
                          <p className="text-[11px] mt-0.5">
                            <span className={ready ? 'text-emerald-600 font-bold' : 'text-gray-600'}>
                              {approved}/{total} Sessions freigegeben
                            </span>
                            {awaiting > 0 && (
                              <span className="text-amber-600 ml-2">· {awaiting} wartet auf Freigabe</span>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{m.status}</span>
                        {ready && !isRunning && !isDone && (
                          <button
                            onClick={(e) => { e.stopPropagation(); startPipeline(m.id, m.company_name || 'Kunde'); }}
                            disabled={busyId === m.id}
                            className="text-[10px] uppercase tracking-wider bg-[#658a07] text-white px-3 py-1.5 hover:bg-[#4f6c05] disabled:opacity-50 inline-flex items-center gap-1"
                            style={{ fontFamily: "'Oswald', sans-serif" }}
                            title="Multi-Agent-Pipeline starten"
                          >
                            {busyId === m.id ? '…' : 'Pipeline starten'}
                          </button>
                        )}
                        {isRunning && (
                          <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-1 rounded animate-pulse">
                            läuft…
                          </span>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); removeMember(m.id); }} className="text-[10px] text-red-600 hover:text-red-800 p-1" title="Aus Kohorte entfernen">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {showAddMember && (
          <AddMembersToCohortModal
            cohortId={cohortId}
            existingMemberLeadIds={new Set(data.members.map((m) => m.lead_id).filter(Boolean))}
            onClose={() => setShowAddMember(false)}
            onAdded={() => { setShowAddMember(false); load(); }}
          />
        )}
    </>);
  return wrap(body);
}

// =============================================================================
// Sub-Modal: Kunden zu Kohorte hinzufügen
// =============================================================================

function AddMembersToCohortModal({ cohortId, existingMemberLeadIds, onClose, onAdded }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/crm/leads`, { headers: authHeaders() })
      .then(({ data }) => {
        // Nur Kunden (kanban_status='kunde') die NICHT schon in dieser Kohorte sind
        const eligible = (data || []).filter((l) =>
          (l.kanban_status === 'kunde') && !existingMemberLeadIds.has(l.id)
        );
        setLeads(eligible);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Konnte Kunden nicht laden'))
      .finally(() => setLoading(false));
  }, [existingMemberLeadIds]);

  const addLead = async (lead) => {
    setAdding(lead.id); setError('');
    try {
      await axios.post(`${API}/admin/leads/${lead.id}/start-modul-1`, {
        cohort_id: cohortId,
        send_mail: true,
      }, { headers: authHeaders() });
      // Nach Add aus Liste entfernen + Cohort-Detail refreshen via parent
      setLeads((arr) => arr.filter((x) => x.id !== lead.id));
    } catch (e) {
      setError(e.response?.data?.detail || 'Hinzufügen fehlgeschlagen');
    }
    setAdding(null);
  };

  const filtered = leads.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (l.company_name || '').toLowerCase().includes(q) ||
      (l.contact_name || '').toLowerCase().includes(q) ||
      (l.contact_email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[560px] max-h-[calc(100vh-2rem)] flex flex-col">
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex justify-between items-start">
          <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>Kunden hinzufügen</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm">
            <AlertTriangle size={14} className="inline mr-2" /> {error}
          </div>
        )}

        <div className="p-4 border-b border-gray-200">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche: Firma, Name, Email …"
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center py-8"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-gray-400 italic text-center py-8">
              {leads.length === 0 ? 'Keine freien Kunden — alle sind schon in dieser Kohorte oder Status ≠ "Kunde".' : 'Kein Treffer für die Suche.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((l) => (
                <li key={l.id} className="flex items-center justify-between p-2 border border-gray-200 hover:bg-gray-50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#1f1f1f] truncate">{l.company_name || '—'}</p>
                    <p className="text-[11px] text-gray-500 truncate">{l.contact_name || '—'} · {l.contact_email || '—'}</p>
                  </div>
                  <button onClick={() => addLead(l)} disabled={adding === l.id}
                    className="text-[10px] uppercase tracking-wider bg-[#0a0a0a] text-[#befa0a] px-3 py-1.5 ml-2 hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {adding === l.id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                    Hinzu
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-gray-200 p-3 flex justify-end">
          <button onClick={onAdded}
            className="bg-[#befa0a] text-[#0a0a0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-lime-300"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Fertig · Aktualisieren
          </button>
        </div>
      </div>
    </div>
  );
}
